import React, { useState, useEffect, useMemo, useCallback } from 'react'
import './profile-detail.scss'
import { investorServices, kycServices, investmentServices, userServices } from '../../services'
import type { PortfolioStatement } from '../../services/investor-service'
import {
  InvestorProps,
  InvestorData,
  Verification,
  UserInvestmentDetails,
  KYCDetailsWithDocument,
  AccountStatus,
  InvestorBankDetails,
} from '../../lib/types'
import { RootState } from '../../store'
import { useSelector, useDispatch } from 'react-redux'
import { isActionAllowed } from '../../lib/permissions/adminActions'
import {
  accessMeetsRequired,
  normalizePermissionsFromLogin,
} from '../../lib/permissions/adminFeatures'
import { INSUFFICIENT_PERMISSION_MESSAGE } from '../../lib/permissions/uiMessages'
import { useParams } from 'react-router-dom'
import { showBanner } from '../../global-state/banner-slice'
import { ProfileDetailsPresenter } from './profile-details-presenter'
import { showModel } from '../../global-state/confirm-model-slice'
import { EngagementEvents, logCrmEvent } from '../../lib/utils/crmEvents'

const getApiErrorMessage = (response: any, fallback: string) =>
  response?.data?.message || response?.data?.error || fallback

const ProfileDetailsContainer = () => {
  const dispatch = useDispatch()
  const { isConfirm } = useSelector((state: RootState) => state.confirmModel)
  const { actionGrants, permissions, role } = useSelector((state: RootState) => state.gbcUser)
  const effectivePermissions = useMemo(
    () => permissions ?? normalizePermissionsFromLogin(role as string, null),
    [permissions, role],
  )
  const [investorState, setInvestor] = useState<InvestorData>()
  const [kycData, setKYCData] = useState<KYCDetailsWithDocument>()
  const [investorBankDetails, setInvestorBankDetails] = useState<InvestorBankDetails>()
  const { investorId } = useParams()
  const [lockStatus, setLockStatus] = useState('')
  const [investmentDetail, setInvestmentDetail] = useState<UserInvestmentDetails>()
  const [portfolioStatements, setPortfolioStatements] = useState<PortfolioStatement[]>([])
  const [isLoadingPortfolioStatements, setIsLoadingPortfolioStatements] = useState(false)
  const [isUploadingPortfolioStatement, setIsUploadingPortfolioStatement] = useState(false)
  const [portfolioStatementsError, setPortfolioStatementsError] = useState('')
  const [pendingDeleteStatementId, setPendingDeleteStatementId] = useState<string | null>(null)

  const canReadPortfolioStatements = accessMeetsRequired(effectivePermissions.investors, 'read')
  const canWritePortfolioStatements = accessMeetsRequired(effectivePermissions.investors, 'write')
  const canReadInvestmentLimitRequests = accessMeetsRequired(effectivePermissions.kyc, 'read')

  const fetchData = async () => {
    const investorIdParam: number = investorId ? parseInt(investorId) : 0
    try {
      const investorData = (await investorServices.getInvestorProfile(
        investorIdParam,
      )) as InvestorProps
      if (investorData)
        setInvestor({
          investor: investorData,
        })
      const response = await investmentServices.getInvestmentDetailForInvestor(investorId as string)
      if (response && response.data && response.status === 200) {
        setInvestmentDetail(response.data)
      }
    } catch (error) {
      console.log(error)
    }
  }

  const fetchPortfolioStatements = useCallback(async () => {
    if (!investorId || !canReadPortfolioStatements) {
      return
    }
    setIsLoadingPortfolioStatements(true)
    setPortfolioStatementsError('')
    try {
      const response = await investorServices.listPortfolioStatements(investorId)
      if (response?.status === 200 && response.data?.statements) {
        setPortfolioStatements(response.data.statements)
      } else {
        setPortfolioStatements([])
        setPortfolioStatementsError(
          getApiErrorMessage(response, 'Failed to load portfolio statements'),
        )
      }
    } catch (error) {
      console.error(error)
      setPortfolioStatementsError('Failed to load portfolio statements')
    } finally {
      setIsLoadingPortfolioStatements(false)
    }
  }, [investorId, canReadPortfolioStatements])

  useEffect(() => {
    const getKYC = async () => {
      const response = await kycServices.getDocumentWithKYC(String(investorId))
      if (!!response && response.data) {
        setKYCData(response.data ?? {})
      }
    }

    const getInvestorBankDetails = async () => {
      const response = await kycServices.getInvestorBankDetails(String(investorId))
      if (!!response && response.data) {
        setInvestorBankDetails(response.data)
      }
    }

    getKYC()
    fetchData()
    getInvestorBankDetails()
  }, [])

  useEffect(() => {
    fetchPortfolioStatements()
  }, [fetchPortfolioStatements])

  useEffect(() => {
    const lockUnlockHandler = async (status: string) => {
      const newStatus = status === 'INACTIVE' || status === 'DORMANT' ? 'ACTIVE' : 'INACTIVE'
      const response = await investorServices.lockUnlockAccount({
        investorId: investorState?.investor?.userId ?? 0,
        account_status: newStatus,
      })
      if (response?.status === 200) {
        setInvestor((prevState: any) => ({
          ...prevState,
          investor: {
            ...prevState.investor,
            accountStatus: newStatus,
          },
        }))
        dispatch(
          showBanner({
            text: 'Client account status updated',
            variant: 'success-banner',
          }),
        )
      } else {
        dispatch(
          showBanner({
            text: getApiErrorMessage(response, 'Failed to update client account status'),
            variant: 'error-banner',
          }),
        )
      }
      setLockStatus('')
    }

    const deleteStatementHandler = async (statementId: string) => {
      if (!investorId) return
      const response = await investorServices.deletePortfolioStatement(investorId, statementId)
      if (response?.status === 200) {
        setPortfolioStatements((prev) => prev.filter((s) => s.id !== statementId))
        dispatch(
          showBanner({
            text: 'Portfolio statement deleted',
            variant: 'success-banner',
          }),
        )
      } else {
        dispatch(
          showBanner({
            text: getApiErrorMessage(response, 'Failed to delete portfolio statement'),
            variant: 'error-banner',
          }),
        )
      }
      setPendingDeleteStatementId(null)
    }

    if (!isConfirm) return
    if (pendingDeleteStatementId) {
      deleteStatementHandler(pendingDeleteStatementId)
      return
    }
    if (investorId && lockStatus) {
      lockUnlockHandler(lockStatus)
    }
  }, [isConfirm])

  const handleBlockUnblock = async (status: AccountStatus) => {
    setPendingDeleteStatementId(null)
    if (status === 'ACTIVE') {
      dispatch(
        showModel({
          name: 'Block Client',
          message: 'Are you sure you want to block the client?',
        }),
      )
    }
    if (status === 'INACTIVE' || status === 'DORMANT') {
      dispatch(
        showModel({
          name: 'Client Unblock',
          message: 'Are you sure you want to unblock the client?',
        }),
      )
    }

    logCrmEvent(
      status === 'ACTIVE' ? EngagementEvents.INVESTOR_BLOCKED : EngagementEvents.INVESTOR_UNBLOCKED,
      {
        investorEmail: investorState?.investor?.email,
        investorId: investorState?.investor?.userId,
      },
    )
    setLockStatus(status)
  }

  const handleInvestmentLimit = async (investmentLimit: number) => {
    // converting dollar value in cents
    const investmentLimitInCents = investmentLimit * 100
    const data: any =
      investorId &&
      ((await investmentServices.setInvestmentLimit(
        parseInt(investorId),
        investmentLimitInCents,
      )) as InvestorProps)

    if (data && data.status === 200) {
      dispatch(
        showBanner({
          text: 'Investment limit updated',
          variant: 'success-banner',
        }),
      )
      fetchData()
    } else {
      dispatch(
        showBanner({
          text:
            (data.data && data.data.message) ||
            'Something went wrong while updating investment limit',
          variant: 'error-banner',
        }),
      )
    }
  }

  const handleSendVerificationEmailLink = async () => {
    const response: any =
      investorState?.investor?.email &&
      !investorState?.investor?.isEmailVerified &&
      (await userServices.sendVerificationLink(
        investorState.investor.email,
        Verification.verifyEmail,
      ))

    logCrmEvent(EngagementEvents.CLICKED_RESEND_VERIFICATION_LINK, {
      investorEmail: investorState?.investor?.email,
      investorId: investorState?.investor?.userId,
    })

    if (!!response && response.status === 200) {
      dispatch(
        showBanner({
          text: 'Email verification link has been sent to email successfully.',
          variant: 'success-banner',
        }),
      )
    } else {
      dispatch(
        showBanner({
          text:
            (response.data && response.data.message) || 'Something went wrong! While verify email',
          variant: 'error-banner',
        }),
      )
    }
  }

  const handleUploadPortfolioStatement = async (file: File, label: string): Promise<boolean> => {
    if (!canWritePortfolioStatements) {
      dispatch(
        showBanner({
          text: INSUFFICIENT_PERMISSION_MESSAGE,
          variant: 'permission-denied-banner',
        }),
      )
      return false
    }
    if (!investorId) return false

    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setPortfolioStatementsError('Only PDF files are allowed')
      return false
    }
    if (file.size > 10 * 1024 * 1024) {
      setPortfolioStatementsError('File must be 10MB or smaller')
      return false
    }

    setIsUploadingPortfolioStatement(true)
    setPortfolioStatementsError('')
    try {
      const response = await investorServices.uploadPortfolioStatement(
        investorId,
        file,
        label || undefined,
      )
      if (response?.status === 201 && response.data) {
        dispatch(
          showBanner({
            text: 'Portfolio statement uploaded',
            variant: 'success-banner',
          }),
        )
        await fetchPortfolioStatements()
        return true
      }
      setPortfolioStatementsError(
        getApiErrorMessage(response, 'Failed to upload portfolio statement'),
      )
      return false
    } catch (error) {
      console.error(error)
      setPortfolioStatementsError('Failed to upload portfolio statement')
      return false
    } finally {
      setIsUploadingPortfolioStatement(false)
    }
  }

  const handleDownloadPortfolioStatement = async (statementId: string) => {
    if (!canReadPortfolioStatements) {
      dispatch(
        showBanner({
          text: INSUFFICIENT_PERMISSION_MESSAGE,
          variant: 'permission-denied-banner',
        }),
      )
      return
    }
    try {
      const response = await investorServices.downloadPortfolioStatement(statementId)
      if (response?.status === 200 && response.data?.url) {
        window.open(response.data.url, '_blank', 'noopener,noreferrer')
      } else {
        dispatch(
          showBanner({
            text: getApiErrorMessage(response, 'Failed to download portfolio statement'),
            variant: 'error-banner',
          }),
        )
      }
    } catch (error) {
      console.error(error)
      dispatch(
        showBanner({
          text: 'Failed to download portfolio statement',
          variant: 'error-banner',
        }),
      )
    }
  }

  const handleDeletePortfolioStatement = (statementId: string) => {
    if (!canWritePortfolioStatements) {
      dispatch(
        showBanner({
          text: INSUFFICIENT_PERMISSION_MESSAGE,
          variant: 'permission-denied-banner',
        }),
      )
      return
    }
    setLockStatus('')
    setPendingDeleteStatementId(statementId)
    dispatch(
      showModel({
        name: 'Delete Portfolio Statement',
        message: 'Are you sure you want to delete this portfolio statement?',
      }),
    )
  }

  const actionAllow = {
    updateInvestmentLimit: isActionAllowed(actionGrants, 'updateInvestmentLimit'),
    resendVerificationLink: isActionAllowed(actionGrants, 'resendVerificationLink'),
    blockInvestor: isActionAllowed(actionGrants, 'blockInvestor'),
    canReadPortfolioStatements,
    canWritePortfolioStatements,
    addInvestorOpportunityTerms: isActionAllowed(actionGrants, 'addInvestorOpportunityTerms'),
    canReadInvestmentLimitRequests,
  }

  return (
    <>
      {investorState && (
        <>
          <ProfileDetailsPresenter
            investorState={investorState}
            investmentDetail={investmentDetail as UserInvestmentDetails}
            kycData={kycData as KYCDetailsWithDocument}
            handleInvestmentLimit={handleInvestmentLimit}
            handleSendVerificationEmailLink={handleSendVerificationEmailLink}
            handleBlockUnblock={handleBlockUnblock}
            actionAllow={actionAllow}
            investorBankDetails={investorBankDetails}
            portfolioStatements={portfolioStatements}
            isLoadingPortfolioStatements={isLoadingPortfolioStatements}
            isUploadingPortfolioStatement={isUploadingPortfolioStatement}
            portfolioStatementsError={portfolioStatementsError}
            onUploadPortfolioStatement={handleUploadPortfolioStatement}
            onDownloadPortfolioStatement={handleDownloadPortfolioStatement}
            onDeletePortfolioStatement={handleDeletePortfolioStatement}
          />
        </>
      )}
    </>
  )
}

export { ProfileDetailsContainer }
